import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Wifi, AlertTriangle } from "@/components/icons";
import { claimUnit } from "@/lib/api";
import { HttpError } from "@/lib/http";

/**
 * Residential driver self-claim dialog. A logged-in driver enters the ID printed
 * on their charger and claims it; the server verifies possession by the charger
 * having reported from the same public IP as this request. Errors are surfaced
 * by the server's machine-readable `error` code (never the message string) —
 * see docs/api/unit-claim.md.
 */

type ClaimError =
  | { kind: "field"; message: string }
  | { kind: "callout"; variant: "already_claimed"; maskedEmail: string | null }
  | { kind: "callout"; variant: "message"; message: string };

function mapClaimError(err: unknown): ClaimError {
  const body =
    err instanceof HttpError && err.body && typeof err.body === "object"
      ? (err.body as Record<string, unknown>)
      : {};
  const code = typeof body.error === "string" ? body.error : "";

  switch (code) {
    case "already_claimed":
      return {
        kind: "callout",
        variant: "already_claimed",
        maskedEmail: typeof body.owner_email_masked === "string" ? body.owner_email_masked : null,
      };
    case "unit_not_found":
      return {
        kind: "field",
        message: "We couldn't find a charger with that ID. Double-check the ID on the label.",
      };
    case "ip_mismatch":
      return {
        kind: "callout",
        variant: "message",
        message:
          "You need to be on the same Wi-Fi network as your charger to add it. Connect this device to your home Wi-Fi and try again.",
      };
    case "unit_unreachable":
      return {
        kind: "callout",
        variant: "message",
        message:
          "This charger hasn't reported in yet. Make sure it's powered on and connected, then try again.",
      };
    case "not_residential":
      return {
        kind: "callout",
        variant: "message",
        message: "This is a shared charger and can't be added to a personal account.",
      };
    case "unit_limit_reached":
      return {
        kind: "callout",
        variant: "message",
        message:
          "Your plan's charger limit has been reached. Remove a charger or upgrade to add more.",
      };
    case "not_residential_user":
      return {
        kind: "callout",
        variant: "message",
        message: "Chargers for your organization are managed by your administrator.",
      };
    default:
      return {
        kind: "callout",
        variant: "message",
        message:
          err instanceof Error && err.message
            ? err.message
            : "Something went wrong. Please try again.",
      };
  }
}

export function ClaimChargerModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [unitId, setUnitId] = useState("");
  const [err, setErr] = useState<ClaimError | null>(null);
  const qc = useQueryClient();

  // Reset transient state whenever the dialog is opened or closed.
  useEffect(() => {
    if (!open) {
      setUnitId("");
      setErr(null);
    }
  }, [open]);

  const claimM = useMutation({
    mutationFn: (id: string) => claimUnit(id),
    onSuccess: (res) => {
      toast.success(res.already_owner ? "You already have this charger" : "Charger claimed");
      // Bring the newly-owned charger into the driver's list.
      qc.invalidateQueries({ queryKey: ["units"] });
      onOpenChange(false);
    },
    onError: (e) => setErr(mapClaimError(e)),
  });

  const trimmed = unitId.trim();
  const submit = () => {
    if (!trimmed || claimM.isPending) return;
    setErr(null);
    claimM.mutate(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Claim a charger</DialogTitle>
          <DialogDescription>
            Enter the ID (28-digit number) printed on the label on the side of your charger to add
            it to your account.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div>
            <Label htmlFor="claim-unit-id">Charger ID</Label>
            <Input
              id="claim-unit-id"
              className="mt-2 font-mono"
              placeholder="e.g. 0910041270000500000000620001"
              value={unitId}
              onChange={(e) => {
                setUnitId(e.target.value);
                if (err) setErr(null);
              }}
              autoFocus
              aria-invalid={err?.kind === "field" || undefined}
            />
            {err?.kind === "field" && (
              <p className="mt-1.5 text-sm text-destructive">{err.message}</p>
            )}
          </div>

          <div className="flex gap-2 rounded-xl bg-primary/10 p-3 text-sm text-primary">
            <Wifi className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Connect this device to the same Wi-Fi network as the charger first — that's how we
              confirm it's yours.
            </span>
          </div>

          {err?.kind === "callout" && (
            <div className="flex gap-2.5 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              {err.variant === "already_claimed" ? (
                <span>
                  This charger is already linked to{" "}
                  <span className="font-mono font-semibold">
                    {err.maskedEmail ?? "another account"}
                  </span>
                  . If that's you on another account, reset that account's password to manage it
                  there — otherwise contact support.
                </span>
              ) : (
                <span>{err.message}</span>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || claimM.isPending}>
              {claimM.isPending ? "Claiming…" : "Claim charger"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
